import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Card,
  Statistic,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Divider,
  Tag,
} from 'antd';
import {
  DownloadOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { downloadReportPDF } from '../../redux/slices/aiReportsSlice';
import ReportTable from './ReportTable';
import {
  formatDate,
  formatMetricValue,
  sanitizeHTML,
} from '../../utils/reportFormatters';
import HDFormatWrapper from './HDFormatWrapper';
import styles from './ReportViewer.module.css';

const { Title, Text, Paragraph } = Typography;

const ReportViewer = React.memo(({ reportData, reportId, isDownloading, htmlContent, htmlLoading }) => {
  const dispatch = useDispatch();

  if (!reportData) {
    return null;
  }

  const { metadata } = reportData;
  const layout = metadata?.layout || {};

  // Handle PDF download
  const handleDownloadPDF = () => {
    dispatch(downloadReportPDF(reportId));
  };

  // Render report HTML content with loading state
  const renderReportContent = () => {
    if (htmlLoading) {
      return (
        <div className={styles.reportContent} style={{ textAlign: 'center', padding: '48px 0' }}>
          <LoadingOutlined style={{ fontSize: 32 }} />
          <p style={{ marginTop: 16 }}>Loading report content...</p>
        </div>
      );
    }

    if (!htmlContent) {
      return null;
    }

    return (
      <div
        className={styles.reportContent}
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(htmlContent) }}
      />
    );
  };

  return (
    <HDFormatWrapper reportData={reportData}>
      <div className={styles.reportViewer}>
        {/* Header Card */}
        <Card className={styles.headerCard}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={3}>{layout.report_title || 'Report'}</Title>
              <Space size="middle">
                <Text type="secondary">
                  <ClockCircleOutlined /> Generated {formatDate(metadata.generated_at)}
                </Text>
                <Tag color="blue">{metadata.record_count} records</Tag>
                {metadata.report_type && (
                  <Tag color="green">{metadata.report_type}</Tag>
                )}
              </Space>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadPDF}
                  loading={isDownloading}
                >
                  Download PDF
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Summary Card */}
        {layout.summary_text && (
          <Card className={styles.summaryCard} title="Summary">
            <Paragraph>{layout.summary_text}</Paragraph>
          </Card>
        )}

        {/* Key Metrics */}
        {layout.key_metrics && Object.keys(layout.key_metrics).length > 0 && (
          <Card className={styles.metricsCard} title="Key Metrics">
            <Row gutter={16}>
              {Object.entries(layout.key_metrics).map(([key, value]) => (
                <Col span={6} key={key}>
                  <Statistic
                    title={key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    value={formatMetricValue(value)}
                  />
                </Col>
              ))}
            </Row>
          </Card>
        )}

        <Divider />

        {/* Main Report Content */}
        {renderReportContent()}


      </div>
    </HDFormatWrapper>
  );
});

export default ReportViewer;
